import React from "react";
import { Box, Paper, Divider } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import { CustomTypography } from "../../Shared/CustomTypography/CustomTypography";
import { CustomBox } from "../../Shared/CustomBox/CustomBox";

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Paper
    sx={{
      border: "1px solid #E0E0E0",
      borderRadius: "6px",
      mb: 3,
      overflow: "hidden",
    }}
    elevation={0}
  >
    <Box sx={{ backgroundColor: "#0A9CF5", px: 2, py: 1 }}>
      <CustomTypography color="#fff" fontWeight={600}>
        {title}
      </CustomTypography>
    </Box>
    <Box p={2}>{children}</Box>
  </Paper>
);

const Field = ({ label, value }: { label: string; value: string }) => (
  <Box>
    <CustomTypography fontWeight={600}>{label}</CustomTypography>
    <CustomTypography color="text.secondary">{value}</CustomTypography>
  </Box>
);

export const ReferralOrder = () => {
const { state } = useLocation();
  const navigate = useNavigate();

  const referral = state?.referral;
  console.log(referral);

  return (
    <Box p={3}>
      {/* Header */}
      <CustomBox mb={3}>
        <CustomTypography
          fontSize={20}
          fontWeight={600}
          sx={{ cursor: "pointer" }}
          onClick={() => navigate(-1)}
        >
          ← Referral Order
        </CustomTypography>
      </CustomBox>

      {/* Referring Doctor */}
      <Section title="Referring Doctor Information">
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <Field label="Name" value={`Dr. ${referral?.referredBy?.name || "N/A"}`} />
          <Field label="Specialty" value={referral?.referredBy?.specialty || "N/A"} />
          <Field label="Contact" value={referral?.referredBy?.email || "N/A"} />
          <Field
            label="Hospital / Clinic"
            value="N/A"
          />
        </Box>
      </Section>

      {/* Patient Information */}
      <Section title="Patient Information">
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <Field label="Name" value="Jane Cooper" />
          <Field label="Age" value="33" />
          <Field label="Gender" value="Female" />
          <Field label="Contact" value="+1 206 555-6789" />
          <Field label="Medical Record (MRN)" value="45231" />
        </Box>
      </Section>

      {/* Referral Doctor */}
      <Section title="Referral Doctor Information">
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <Field label="Name" value={`Dr. ${referral?.referredTo?.name || "N/A"}`} />
          <Field label="Specialty" value={referral?.referredTo?.specialty || "N/A"} />
          <Field label="Contact" value={referral?.referredTo?.email || "N/A"} />
          <Field
            label="Hospital / Clinic"
            value="N/A"
          />
        </Box>
      </Section>

      {/* Clinical Details */}
      <Section title="Clinical Details">
        <CustomTypography fontWeight={600}>
          Presenting Complaint
        </CustomTypography>
        <CustomTypography color="text.secondary" mb={2}>
          Lower abdominal pain, increased frequency of urination, burning
          micturition, occasional hematuria.
        </CustomTypography>

        <Divider sx={{ my: 2 }} />

        <CustomTypography fontWeight={600}>
          History of Present Illness
        </CustomTypography>
        <CustomTypography color="text.secondary" mb={2}>
          Symptoms started 2 weeks ago, gradually worsening. Pain radiates
          towards the groin. Reports nocturia (3–4 times/night). No history
          of trauma. Previous hospitalization for urinary tract infection
          1 year ago.
        </CustomTypography>

        <Divider sx={{ my: 2 }} />

        <CustomTypography fontWeight={600}>
          Past Medical History
        </CustomTypography>
        <CustomTypography color="text.secondary" mb={2}>
          Hypertension (10 years), Type 2 Diabetes Mellitus (8 years).
        </CustomTypography>

        <Divider sx={{ my: 2 }} />

        <CustomTypography fontWeight={600}>
          Current Medications
        </CustomTypography>
        <CustomTypography color="text.secondary" mb={2}>
          Aspirin 75 mg daily, Metoprolol 50 mg BID, Metformin 500 mg BID.
        </CustomTypography>

        <Divider sx={{ my: 2 }} />

        <CustomTypography fontWeight={600}>Allergies</CustomTypography>
        <CustomTypography color="text.secondary" mb={2}>
          No known drug allergies.
        </CustomTypography>

        <Divider sx={{ my: 2 }} />

        <CustomTypography fontWeight={600}>Vital Signs</CustomTypography>
        <CustomTypography color="text.secondary" mb={2}>
          BP: 150/95 mmHg, HR: 92 bpm, Temp: 98.4°F, SpO₂: 95%.
        </CustomTypography>

        <Divider sx={{ my: 2 }} />

        <CustomTypography fontWeight={600}>
          Investigations
        </CustomTypography>
        <CustomTypography color="text.secondary">
          • Urinalysis: Presence of RBCs and WBCs <br />
          • Ultrasound KUB: Mild left hydronephrosis, possible ureteric
          calculus (6 mm) <br />
          • Serum Creatinine: Mildly elevated
        </CustomTypography>
      </Section>

      {/* Footer */}
      <Section title="Reason for Referral">
        <CustomTypography color="text.secondary" mb={2}>
          {referral?.reason || "N/A"}
        </CustomTypography>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <Box>
            <CustomTypography fontWeight={600}>Date</CustomTypography>
            <CustomTypography color="text.secondary">
              {dayjs(referral?.date).format("DD-MMM-YYYY")}
            </CustomTypography>
          </Box>
          <Box>
            <CustomTypography fontWeight={600}>
              Signature
            </CustomTypography>
            <CustomTypography color="text.secondary">
              ______________________
            </CustomTypography>
          </Box>
        </Box>
      </Section>
    </Box>
  );
};

export default ReferralOrder;
